import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api, { normalizeAvatarUrl } from '../services/api'
import EmployeeAvatar from '../components/EmployeeAvatar'
import Icon from '../components/Icon'
import { FormGroup, Input, Button, RoleBadge } from '../components/ui'
import { translateApiError } from '../utils/errorMessages'
import './Settings.css'

/** Включить блок «Уведомления» на странице настроек */
const SHOW_NOTIFICATIONS_SECTION = false

function Settings() {
  const { user, fetchUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: false
  })
  const [employee, setEmployee] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })

  useEffect(() => {
    if (user) {
      fetchEmployeeData()
      // Обновляем preview только если нет локального preview (выбранного файла)
      // Это позволяет показывать превью выбранного файла до загрузки
      if (!avatarFile && user.avatar_url) {
        setAvatarPreview(user.avatar_url)
      } else if (!avatarFile && !user.avatar_url) {
        // Если нет avatar_url и нет выбранного файла, сбрасываем preview
        setAvatarPreview(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const fetchEmployeeData = async () => {
    if (user?.employee_id) {
      try {
        const response = await api.get(`/company/team/${user.employee_id}`)
        if (response.data && response.data.employee) {
          setEmployee(response.data.employee)
        }
      } catch (error) {
        console.error('Error fetching employee data:', error)
      }
    }
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Проверяем размер файла (макс 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Размер файла не должен превышать 5MB' })
        return
      }

      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Выберите файл изображения' })
        return
      }

      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      setMessage({ type: 'error', text: 'Выберите файл для загрузки' })
      return
    }

    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('avatar', avatarFile)

      // Не указываем Content-Type явно - браузер сам установит правильный заголовок с boundary
      const response = await api.post('/auth/avatar', formData, {
        headers: {
          'Content-Type': undefined  // Удаляем Content-Type, чтобы браузер сам установил multipart/form-data с boundary
        },
        withCredentials: true
      })

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Аватар успешно загружен' })
        setAvatarFile(null)
        
        // Обновляем preview из ответа сервера
        if (response.data.avatar_url) {
          setAvatarPreview(response.data.avatar_url)
        }
        
        // Обновляем данные пользователя
        if (fetchUser) {
          await fetchUser()
        }
        // Если пользователь связан с сотрудником, обновляем данные сотрудника
        if (user?.employee_id) {
          await fetchEmployeeData()
        }
      }
    } catch (error) {
      console.error('Error uploading avatar:', error)
      setMessage({ 
        type: 'error', 
        text: translateApiError(error.response?.data?.error) || 'Ошибка загрузки аватара' 
      })
    } finally {
      setLoading(false)
    }
  }

  const calculatePasswordStrength = (password) => {
    if (!password) return 0
    
    let strength = 0
    if (password.length >= 6) strength++
    if (password.length >= 8) strength++
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++
    if (/\d/.test(password)) strength++
    if (/[^a-zA-Z\d]/.test(password)) strength++
    
    return Math.min(strength, 4)
  }

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(passwordData.newPassword))
  }, [passwordData.newPassword])

  const getPasswordStrengthLabel = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { text: 'Слабый', color: 'var(--color-error)' }
      case 2:
        return { text: 'Средний', color: 'var(--color-warning)' }
      case 3:
        return { text: 'Хороший', color: '#4CAF50' }
      case 4:
        return { text: 'Отличный', color: 'var(--color-success)' }
      default:
        return { text: '', color: 'transparent' }
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setMessage({ type: 'error', text: 'Заполните все поля' })
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Новые пароли не совпадают' })
      return
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Пароль должен содержать минимум 6 символов' })
      return
    }

    try {
      setLoading(true)
      const response = await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Пароль успешно изменен' })
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
      }
    } catch (error) {
      console.error('Error changing password:', error)
      setMessage({ 
        type: 'error', 
        text: translateApiError(error.response?.data?.error) || 'Ошибка изменения пароля' 
      })
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
    // Заглушка - здесь будет сохранение настроек уведомлений
    setMessage({ type: 'info', text: 'Настройки уведомлений сохранены (заглушка)' })
  }

  // Создаем объект employee для аватара
  const avatarEmployee = employee || (user?.avatar_url ? {
    full_name: user.login,
    avatar_url: user.avatar_url || avatarPreview,
    user: { avatar_url: user.avatar_url || avatarPreview }
  } : {
    full_name: user?.login || 'Пользователь'
  })

  return (
    <div className="settings-page">
      {/* Hero секция */}
      <div className="settings-hero">
        <div className="settings-hero-avatar-section">
          <div className="settings-hero-avatar-wrapper">
            <div className="settings-hero-avatar">
              {/* Всегда показываем EmployeeAvatar как заглушку */}
              <div className="settings-hero-avatar-placeholder">
                <EmployeeAvatar
                  employee={avatarEmployee}
                  size="large"
                  showStatus={false}
                />
              </div>
              {/* Показываем изображение поверх, если оно есть и загрузилось */}
              {(avatarPreview || user?.avatar_url) && (
                <img 
                  src={normalizeAvatarUrl(avatarPreview || user?.avatar_url)} 
                  alt="Аватар" 
                  className="settings-hero-avatar-image"
                  onError={(e) => {
                    // Если изображение не загрузилось, скрываем его
                    e.target.style.display = 'none'
                  }}
                />
              )}
            </div>
            <div className="settings-hero-avatar-overlay">
              <label className="settings-hero-avatar-edit-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <Icon name="edit" size={20} />
              </label>
            </div>
          </div>
          {avatarFile && (
            <div className="settings-hero-avatar-controls">
              <Button
                type="button"
                variant="primary"
                onClick={handleAvatarUpload}
                disabled={loading}
                className="settings-hero-avatar-controls-btn"
              >
                {loading ? 'Загрузка...' : 'Загрузить'}
              </Button>
            </div>
          )}
        </div>
        <div className="settings-hero-info">
          <div className="settings-hero-name">
            {employee?.full_name || user?.login || 'Пользователь'}
          </div>
          {employee ? (
            <>
              <div className="settings-hero-employee-info">
                {employee.department && (
                  <span className="settings-hero-department">
                    <Icon name="building" size={14} />
                    {employee.department}
                  </span>
                )}
                {employee.role && (
                  <span className="settings-hero-employee-role">
                    {employee.role}
                  </span>
                )}
              </div>
              <div className="settings-hero-system-info">
                <RoleBadge role={user?.role} />
                <span className="settings-hero-login">
                  <Icon name="users" size={14} />
                  {user?.login}
                </span>
              </div>
            </>
          ) : (
            <div className="settings-hero-system-info">
              <RoleBadge role={user?.role} />
            </div>
          )}
        </div>
      </div>

      {message.text && (
        <div className={`settings-message settings-message-${message.type}`}>
          {message.text}
          <button 
            className="message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}

      <div className="settings-content">
        {/* Смена пароля */}
        <div className="settings-section">
          <h2>
            <Icon name="lock" size={20} />
            Безопасность
          </h2>
          <div className="settings-card">
            <form onSubmit={handlePasswordChange} className="password-form">
              <FormGroup label="Текущий пароль:" htmlFor="currentPassword">
                <Input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup label="Новый пароль:" htmlFor="newPassword">
                <>
                  <Input
                    type="password"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                  {passwordData.newPassword && (
                    <div className="password-strength">
                      <div className="password-strength-bars">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`password-strength-bar ${
                              level <= passwordStrength ? 'active' : ''
                            }`}
                            style={{
                              backgroundColor: level <= passwordStrength
                                ? getPasswordStrengthLabel(passwordStrength).color
                                : 'var(--color-border)'
                            }}
                          />
                        ))}
                      </div>
                      <span
                        className="password-strength-label"
                        style={{ color: getPasswordStrengthLabel(passwordStrength).color }}
                      >
                        {getPasswordStrengthLabel(passwordStrength).text}
                      </span>
                    </div>
                  )}
                </>
              </FormGroup>
              <FormGroup label="Подтвердите новый пароль:" htmlFor="confirmPassword">
                <Input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                />
              </FormGroup>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Сохранение...' : 'Изменить пароль'}
              </Button>
            </form>
          </div>
        </div>

        {/* Уведомления — скрыто; чтобы показать, задайте SHOW_NOTIFICATIONS_SECTION = true */}
        {SHOW_NOTIFICATIONS_SECTION && (
        <div className="settings-section">
          <h2>
            <Icon name="settings" size={20} />
            Уведомления
          </h2>
          <div className="settings-card">
            <p className="settings-hint" style={{ marginBottom: 'var(--spacing-lg)' }}>
              Настройте способы получения уведомлений (заглушка)
            </p>
            <div className="notifications-list">
              <div className="notification-item">
                <div className="notification-info">
                  <span className="notification-label">Email уведомления</span>
                  <span className="notification-description">
                    Получать уведомления на электронную почту
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notifications.email}
                    onChange={() => handleNotificationChange('email')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="notification-item">
                <div className="notification-info">
                  <span className="notification-label">Push уведомления</span>
                  <span className="notification-description">
                    Получать push-уведомления в браузере
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notifications.push}
                    onChange={() => handleNotificationChange('push')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="notification-item">
                <div className="notification-info">
                  <span className="notification-label">SMS уведомления</span>
                  <span className="notification-description">
                    Получать уведомления по SMS
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notifications.sms}
                    onChange={() => handleNotificationChange('sms')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

export default Settings

